'use client';
import React, { useState } from 'react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import Link from 'next/link';

const accountTypes = [
  { id: 'individual', label: 'فرد', desc: 'مواطن يمني أو زائر أجنبي', icon: '👤' },
  { id: 'entity', label: 'جهة / شركة', desc: 'جهة حكومية أو شركة خاصة', icon: '🏢' },
  { id: 'mission', label: 'بعثة دبلوماسية', desc: 'سفارة أو بعثة دبلوماسية', icon: '🏤' },
];

export default function SignupPage() {
  const [step, setStep] = useState(1);
  const [accountType, setAccountType] = useState('individual');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    nationalId: '',
    password: '',
    confirmPassword: '',
    entityName: '',
    entityType: '',
  });

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    setStep(2);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep(3);
  };

  return (
    <div className="min-h-screen flex flex-col font-tajawal" dir="rtl">
      <Navbar />
      <main className="flex-1 bg-background flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-lg">
          {/* Logo */}
          <div className="text-center mb-8">
            <img
              src="https://media.base44.com/images/public/69ed0fe31d3c9ede3b821281/406079c5f_Emblem_of_Yemen_2svg.png"
              alt="شعار اليمن"
              className="w-16 h-16 object-contain mx-auto mb-3"
            />
            <h1 className="text-2xl font-bold text-primary">إنشاء حساب جديد</h1>
            <p className="text-sm text-muted-foreground mt-1">منصة تصريح — وزارة الخارجية اليمنية</p>
          </div>

          {/* Steps */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                    step >= s ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {step > s ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : s}
                </div>
                {s < 3 && <div className={`w-12 h-0.5 ${step > s ? 'bg-primary' : 'bg-border'}`}></div>}
              </React.Fragment>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-border shadow-gov-lg p-8">
            {step === 1 && (
              <div>
                <h2 className="text-lg font-bold text-foreground mb-5">اختر نوع الحساب</h2>
                <div className="space-y-3 mb-6">
                  {accountTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setAccountType(type.id)}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-right ${
                        accountType === type.id
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/30'
                      }`}
                    >
                      <span className="text-2xl">{type.icon}</span>
                      <div className="flex-1">
                        <div className="font-semibold text-sm text-foreground">{type.label}</div>
                        <div className="text-xs text-muted-foreground">{type.desc}</div>
                      </div>
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          accountType === type.id ? 'border-primary' : 'border-border'
                        }`}
                      >
                        {accountType === type.id && <div className="w-2.5 h-2.5 rounded-full bg-primary"></div>}
                      </div>
                    </button>
                  ))}
                </div>
                <button onClick={() => setStep(2)} className="gov-btn-primary w-full justify-center">
                  التالي
                </button>
              </div>
            )}

            {step === 2 && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <h2 className="text-lg font-bold text-foreground mb-5">بيانات الحساب</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">الاسم الكامل *</label>
                    <input type="text" required className="input-gov" placeholder="محمد علي" value={formData.fullName} onChange={(e) => setFormData({ ...formData, fullName: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">رقم الهوية *</label>
                    <input type="text" required className="input-gov" placeholder="XXXXXXXXXX" value={formData.nationalId} onChange={(e) => setFormData({ ...formData, nationalId: e.target.value })} dir="ltr" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">البريد الإلكتروني *</label>
                  <input type="email" required className="input-gov" placeholder="example@email.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} dir="ltr" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">رقم الهاتف</label>
                  <input type="tel" className="input-gov" placeholder="+967 XXX XXX XXX" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} dir="ltr" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">كلمة المرور *</label>
                    <input type="password" required className="input-gov" placeholder="••••••••" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">تأكيد كلمة المرور *</label>
                    <input type="password" required className="input-gov" placeholder="••••••••" value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} />
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setStep(1)} className="gov-btn-outline flex-1 justify-center">رجوع</button>
                  <button type="submit" className="gov-btn-primary flex-1 justify-center">إنشاء الحساب</button>
                </div>
              </form>
            )}

            {step === 3 && (
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">تم إنشاء الحساب بنجاح</h3>
                <p className="text-sm text-muted-foreground mb-6">تحقق من بريدك الإلكتروني لتفعيل الحساب</p>
                <Link href="/login">
                  <button className="gov-btn-primary">تسجيل الدخول</button>
                </Link>
              </div>
            )}
          </div>

          {step !== 3 && (
            <div className="mt-4 text-center">
              <p className="text-sm text-muted-foreground">
                لديك حساب بالفعل؟{' '}
                <Link href="/login" className="text-accent font-semibold hover:underline">
                  تسجيل الدخول
                </Link>
              </p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
